import { DaysOfWeek } from "../menuPlanningTypes"

import {
    DishParameterKey,
    DishParameterType,
    MenuTemplateChannel,
    MenuTemplateDetails,
    MenuTemplateId,
    MenuTemplateListItem,
} from "../templates/menuTemplatesTypes"

// Mock templates until the week menu backend exists; swap for fetchMenuTemplates when live.
// TODO: replace with fetchMenuTemplates/fetchMenuTemplate once the week menu API exists.
export const MOCK_MENU_TEMPLATES: ReadonlyArray<MenuTemplateListItem> = [
    {
        id: "tpl_01HZQ8N7",
        name: "Standard lunch",
        channels: [MenuTemplateChannel.MOBILE_APP, MenuTemplateChannel.CANTEEN_DISPLAY],
        categoryCount: 3,
        dishCount: 6,
        updatedAt: "2026-07-20T09:14:03Z",
    },
    {
        id: "tpl_01HZQ8P2",
        name: "Holiday lunch",
        channels: [MenuTemplateChannel.CANTEEN_DISPLAY, MenuTemplateChannel.PRINT],
        categoryCount: 2,
        dishCount: 3,
        updatedAt: "2026-06-02T11:40:55Z",
    },
]

const ALL_WEEKDAYS = [
    DaysOfWeek.MONDAY,
    DaysOfWeek.TUESDAY,
    DaysOfWeek.WEDNESDAY,
    DaysOfWeek.THURSDAY,
    DaysOfWeek.FRIDAY,
    DaysOfWeek.SATURDAY,
    DaysOfWeek.SUNDAY,
]

const STANDARD_LUNCH: MenuTemplateDetails = {
    id: "tpl_01HZQ8N7",
    name: "Standard lunch",
    channels: [MenuTemplateChannel.MOBILE_APP, MenuTemplateChannel.CANTEEN_DISPLAY],
    categories: [
        {
            name: "Warm dishes",
            dishes: [
                {
                    name: "Soup",
                    availability: { weekDays: ALL_WEEKDAYS },
                    parameters: [
                        {
                            key: DishParameterKey.PRICE,
                            type: DishParameterType.LOCKED,
                            defaultValue: "35",
                            sameAllWeek: true,
                        },
                        { key: DishParameterKey.DESCRIPTION, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.ALLERGENS, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.AVAILABILITY, type: DishParameterType.PREFILLED },
                        {
                            key: DishParameterKey.IMAGE,
                            type: DishParameterType.LOCKED,
                            defaultValue: "soup-of-the-day.jpg",
                        },
                    ],
                },
                {
                    name: "Today's warm dish",
                    availability: { weekDays: ALL_WEEKDAYS },
                    parameters: [
                        { key: DishParameterKey.PRICE, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.DESCRIPTION, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.ALLERGENS, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.AVAILABILITY, type: DishParameterType.PREFILLED },
                        { key: DishParameterKey.MARKETING_TAG, type: DishParameterType.OPTIONAL },
                        { key: DishParameterKey.IMAGE, type: DishParameterType.OPTIONAL },
                    ],
                },
                {
                    name: "Today's vegetarian dish",
                    availability: { weekDays: ALL_WEEKDAYS },
                    parameters: [
                        { key: DishParameterKey.PRICE, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.DESCRIPTION, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.ALLERGENS, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.AVAILABILITY, type: DishParameterType.PREFILLED },
                        { key: DishParameterKey.MARKETING_TAG, type: DishParameterType.OPTIONAL },
                        { key: DishParameterKey.IMAGE, type: DishParameterType.OPTIONAL },
                    ],
                },
            ],
        },
        {
            name: "Salads",
            dishes: [
                {
                    name: "Mixed greens",
                    availability: { weekDays: ALL_WEEKDAYS },
                    parameters: [
                        { key: DishParameterKey.DESCRIPTION, type: DishParameterType.OPTIONAL },
                        { key: DishParameterKey.ALLERGENS, type: DishParameterType.OPTIONAL },
                        { key: DishParameterKey.AVAILABILITY, type: DishParameterType.PREFILLED },
                    ],
                },
                {
                    name: "Grain salad",
                    availability: {
                        weekDays: [DaysOfWeek.MONDAY, DaysOfWeek.WEDNESDAY, DaysOfWeek.FRIDAY],
                    },
                    parameters: [
                        { key: DishParameterKey.DESCRIPTION, type: DishParameterType.OPTIONAL },
                        { key: DishParameterKey.AVAILABILITY, type: DishParameterType.PREFILLED },
                    ],
                },
            ],
        },
        {
            name: "Cold cuts",
            dishes: [
                {
                    name: "Daily selection",
                    availability: { weekDays: ALL_WEEKDAYS },
                    parameters: [
                        {
                            key: DishParameterKey.DESCRIPTION,
                            type: DishParameterType.PREFILLED,
                            defaultValue: "Ham, salami, brie, hummus — standard selection.",
                        },
                        {
                            key: DishParameterKey.ALLERGENS,
                            type: DishParameterType.PREFILLED,
                            defaultValue: "Gluten",
                        },
                        { key: DishParameterKey.AVAILABILITY, type: DishParameterType.PREFILLED },
                    ],
                },
            ],
        },
    ],
}

const HOLIDAY_LUNCH: MenuTemplateDetails = {
    id: "tpl_01HZQ8P2",
    name: "Holiday lunch",
    channels: [MenuTemplateChannel.CANTEEN_DISPLAY, MenuTemplateChannel.PRINT],
    categories: [
        {
            name: "Festive starters",
            dishes: [
                {
                    name: "Starter",
                    availability: { weekDays: ALL_WEEKDAYS },
                    parameters: [
                        {
                            key: DishParameterKey.PRICE,
                            type: DishParameterType.LOCKED,
                            defaultValue: "45",
                            sameAllWeek: true,
                        },
                        { key: DishParameterKey.DESCRIPTION, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.ALLERGENS, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.AVAILABILITY, type: DishParameterType.PREFILLED },
                    ],
                },
            ],
        },
        {
            name: "Holiday mains",
            dishes: [
                {
                    name: "Main course",
                    availability: { weekDays: ALL_WEEKDAYS },
                    parameters: [
                        { key: DishParameterKey.PRICE, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.DESCRIPTION, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.ALLERGENS, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.AVAILABILITY, type: DishParameterType.PREFILLED },
                        { key: DishParameterKey.MARKETING_TAG, type: DishParameterType.OPTIONAL },
                        { key: DishParameterKey.IMAGE, type: DishParameterType.OPTIONAL },
                    ],
                },
                {
                    name: "Vegetarian main",
                    availability: { weekDays: ALL_WEEKDAYS },
                    parameters: [
                        { key: DishParameterKey.PRICE, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.DESCRIPTION, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.ALLERGENS, type: DishParameterType.MANDATORY },
                        { key: DishParameterKey.AVAILABILITY, type: DishParameterType.PREFILLED },
                    ],
                },
            ],
        },
    ],
}

const MOCK_TEMPLATE_DETAILS: ReadonlyArray<MenuTemplateDetails> = [STANDARD_LUNCH, HOLIDAY_LUNCH]

export function mockTemplateDetailsFor(templateId: MenuTemplateId): MenuTemplateDetails | undefined {
    return MOCK_TEMPLATE_DETAILS.find((details) => details.id === templateId)
}
